import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { Trans } from '@/UI/Trans/Trans';
import {
  AppDialogSection,
  AppDialog,
  AppDialogContent,
  AppDialogActions,
  AppDialogHeader,
  AppDialogTitle,
  AppDialogXBtn,
} from '@/UI/AppDialog/AppDialog';
import { TextStdRg } from '@/UI/AppTypography/AppTypography';
import { I18N_KEYS } from '@/language/keys';
import { AppCheckbox } from '@/UI/AppCheckbox/AppCheckbox';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { AsteriskIcon } from '@/UI/Icons';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { Flex, FlexCol } from '@/UI/styles';

/** A run of at least this many credits asks for confirmation first, unless the user chose not to be asked. */
export const EXPENSIVE_RUN_CREDITS = 100;
/** Where the user's "don't ask again" choice is saved. The canvas and the generate page share it. */
export const ALLOW_EXPENSIVE_RUN_KEY = 'allowRunExpensiveRun';

interface ExpensiveRunModalProps {
  onConfirm: () => void;
  onClose: () => void;
  open?: boolean;
  cost: number;
}

export const ExpensiveRunModal = ({ onClose, onConfirm, open = false, cost }: ExpensiveRunModalProps) => {
  const { t } = useTranslation();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const { getItem, setItem } = useLocalStorage<boolean>(ALLOW_EXPENSIVE_RUN_KEY);
  const [checked, setChecked] = useState(getItem() ?? false);

  useEffect(() => {
    if (open) {
      setChecked(getItem() ?? false);
    }
  }, [open, getItem]);

  return (
    <AppDialog
      data-testid="expensive-run-warning-modal"
      open={open}
      onClose={onClose}
      aria-labelledby="expensive-run-warning-modal"
      width="md"
      slotProps={{
        paper: {
          sx: { background: token.bg },
        },
      }}
    >
      <AppDialogSection>
        <AppDialogHeader sx={{ display: 'flex', pt: 6 }}>
          <AppDialogTitle>{t(I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.TITLE)}</AppDialogTitle>
          <AppDialogXBtn onClick={onClose} size={20} sx={{ position: 'absolute', top: 12, right: 12 }} />
        </AppDialogHeader>
        <AppDialogContent>
          <FlexCol sx={{ gap: 2 }}>
            <TextStdRg color={token.text} sx={{ whiteSpace: 'pre-line' }}>
              <Trans
                i18nKey={I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.SUBTITLE}
                components={{
                  b: <b />,
                  // The FPL credit glyph renders at its own 24px size (the legacy width/height are dropped),
                  // so it needs `vertical-align: middle` to sit on the text's optical center instead of
                  // hanging off the baseline the way the smaller legacy icon does.
                  icon: isFplMigrationEnabled ? (
                    <AsteriskIcon style={{ verticalAlign: 'middle' }} />
                  ) : (
                    <AsteriskIcon width={14} height={14} style={{ verticalAlign: 'sub' }} />
                  ),
                }}
                values={{
                  cost,
                }}
              />
            </TextStdRg>
          </FlexCol>
        </AppDialogContent>
        <AppDialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
          <AppCheckbox
            checked={checked}
            onChange={(_, newChecked) => setChecked(newChecked)}
            label={t(I18N_KEYS.COMMON_COMPONENTS.EXPENSIVE_RUN_MODAL.CHECKBOX_LABEL)}
          />
          <Flex sx={{ gap: 1 }}>
            <ButtonContained mode="outlined" onClick={onClose} size="small">
              {t(I18N_KEYS.RECIPE_MAIN.FLOW.PRICY_MODEL_MODAL.CTA_CANCEL)}
            </ButtonContained>
            <ButtonContained
              mode="filled-light"
              onClick={() => {
                setItem(checked);
                onConfirm();
              }}
              size="small"
            >
              {t(I18N_KEYS.RECIPE_MAIN.FLOW.PRICY_MODEL_MODAL.CTA_CONFIRM)}
            </ButtonContained>
          </Flex>
        </AppDialogActions>
      </AppDialogSection>
    </AppDialog>
  );
};
